/* eslint-disable react/no-array-index-key */
/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import PercentageSeriesTooltip from "components/charts/modern/tooltip/PercentageSeriesTooltip";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import aeonik from "components/utils/aeonik";
import type Highcharts from "highcharts/highstock";
import SayOnPayHistoryQuery from "lib/graphql/queries/SayOnPayHistoryQuery";
import type {
  ISayOnPayHistoryQuery,
  ISayOnPayHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/SayOnPayHistoryQuery.generated";
import uniqBy from "lodash/uniqBy";
import { renderToStaticMarkup } from "react-dom/server";
import colors from "tailwindcss/colors";
import { ISecurityCountry } from "types/graphqlTypes";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import SadFaceIcon from "components/icons/SadFaceIcon";
import { FormattedMessage } from "react-intl";
import { useMinWidthTailwindBreakpoints } from "components/ui/hooks/useTailwindBreakpoints";
import getContourColors from "components/stocks/modern/OperatingSegments/getContourColors";
import type { SeriesOptionsType } from "highcharts/highstock";

export default function SayOnPayChart() {
  const breakpoints = useMinWidthTailwindBreakpoints();
  const issuer = useRootIssuer();

  const tickerSymbol = issuer.__typename === "AmericanCompanyIssuer" ? issuer.primaryTickerSymbol : "";
  const { data, loading } = useQuery<ISayOnPayHistoryQuery, ISayOnPayHistoryQueryVariables>(SayOnPayHistoryQuery, {
    skip: !tickerSymbol,
    variables: {
      country: ISecurityCountry.Us,
      tickerSymbol: issuer.__typename === "AmericanCompanyIssuer" ? issuer.primaryTickerSymbol : "",
    },
  });

  if (loading) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  if (
    !tickerSymbol ||
    !data ||
    !data.issuerByTickerSymbol ||
    data.issuerByTickerSymbol.__typename !== "AmericanCompanyIssuer" ||
    data.issuerByTickerSymbol.sayOnPayHistory.length === 0
  ) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  const sayOnPayHistory = uniqBy(
    [...(data?.issuerByTickerSymbol.sayOnPayHistory || [])].reverse(),
    (node) => node.fiscalYear,
  );

  const sayOnPayFor = sayOnPayHistory.map((node) => ({
    x: node.fiscalYear,
    y: node.sayOnPayFor || 0,
  }));
  const sayOnPayAgainst = sayOnPayHistory.map((node) => ({
    x: node.fiscalYear,
    y: node.sayOnPayAgainst || 0,
  }));
  const sayOnPayAbstention = sayOnPayHistory.map((node) => ({
    x: node.fiscalYear,
    y: node.sayOnPayAbstention || 0,
  }));
  const sayOnPayBrokerNonVotes = sayOnPayHistory.map((node) => ({
    x: node.fiscalYear,
    y: node.sayOnPayBrokerNonVotes || 0,
  }));
  const atFiscalYears = sayOnPayHistory.map((node) => {
    return String(node.fiscalYear);
  });

  const hasOnlyOneValue = sayOnPayHistory.length === 1;
  const chartType = hasOnlyOneValue ? "pie" : "column";

  const series = [
    {
      type: chartType,
      data: sayOnPayFor,
      name: "For",
      color: colors.lime[100],
      borderColor: colors.lime[600],
      borderWidth: 1.5,
    },
    {
      type: chartType,
      data: sayOnPayBrokerNonVotes,
      name: "Broker non-votes",
      color: colors.indigo[100],
      borderColor: colors.indigo[600],
      borderWidth: 1.5,
    },
    {
      type: chartType,
      data: sayOnPayAgainst,
      name: "Against",
      color: colors.pink[100],
      borderColor: colors.pink[600],
      borderWidth: 1.5,
    },
    {
      type: chartType,
      data: sayOnPayAbstention,
      name: "Abstention",
      color: colors.slate[100],
      borderColor: colors.slate[600],
      borderWidth: 1.5,
    },
  ];

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      type: chartType,
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      plotBorderWidth: 1,
      plotBorderColor: colors.slate[100],
      height: null,
    },
    scrollbar: {
      enabled: false,
    },
    legend: {
      align: "left",
      enabled: true,
      padding: 0,
      alignColumns: true,
      itemDistance: 16,
    },
    xAxis: {
      categories: atFiscalYears,
      gridLineWidth: 1,
      gridLineColor: colors.slate[100],
      lineWidth: 0,
      lineColor: colors.slate[100],
      tickWidth: 0,
      labels: {
        format: "{value}",
        enabled: true,
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      crosshair: {
        color: colors.slate[300],
        width: 1,
      },
    },
    yAxis: {
      lineWidth: 0,
      lineColor: colors.slate[100],
      gridLineWidth: 0,
      gridLineColor: colors.slate[100],
      tickWidth: 0,
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
        formatter() {
          const valueFormatter = new Intl.NumberFormat("en", {
            notation: "compact",
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          });
          return valueFormatter.format(parseFloat(String(this.value)));
        },
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series: hasOnlyOneValue
      ? [
          {
            type: "pie",
            size: "70%",
            innerSize: "50%",
            borderRadius: 4,
            showInLegend: true,
            data: series.map((node, index) => {
              const theme = getContourColors(index);

              const color = theme.background;
              const borderColor = theme.text;

              return {
                name: node.name,
                y: node.data[0].y,
                color,
                borderColor,
                borderWidth: 1.5,
                custom: node,
                dataLabels: {
                  enabled: breakpoints.lg || breakpoints.xl,
                  style: {
                    color: colors.slate[800],
                    fontFamily: aeonik.style.fontFamily,
                    fontWeight: "500",
                    fontSize: "13px",
                    textOutline: "none",
                    textOverflow: "ellipsis",
                    width: 240,
                  },
                },
              };
            }),
          },
        ]
      : (series as SeriesOptionsType[]),
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[300],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      hideDelay: 0,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        return renderToStaticMarkup(<PercentageSeriesTooltip context={this} />);
      },
    },
    plotOptions: {
      series: {
        stacking: "normal",
        states: {
          hover: {
            lineWidth: 2,
          },
        },
      },
      column: {
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
  };

  return (
    <OnlyPremiumRender>
      <HighchartsChart options={options} className="h-96 xl:h-full" />
    </OnlyPremiumRender>
  );
}
